import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Checkbox from "@cs/components/src/Checkbox";
import Input from "@cs/components/src/Input";
import ListInput from "@cs/components/src/ListInput";
import Select, { Option } from "@cs/components/src/Select";
import OptionInput from "@cs/components/src/OptionInput";
import ApiService from "../../services/ApiService";
import Button from "@cs/components/src/Button";
import { ExternalLink } from "react-feather";
import VariableInputForm from "./VariableInputForm";
import Description from "@cs/components/src/Description";

type Props = {
  variable: VariableConfig;
  variables: VariableInstance[];
  value: any;
  onChange: (value: any) => void;
  systemDomainId?: string;
  label?: string;
  config: PluginConfigItem | ScriptConfigItem | ListInputConfig;
};

const Tooltip = styled.div`
  z-index: 100000;
  width: 100%;
  position: absolute;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
  transition: all 100ms;

  > span {
    background: var(--blue090);
    color: var(--gray500);
    padding: 8px;
    max-width: 100%;
    border-radius: 3px;
    font-size: 13px;
    word-break: break-word;
    display: block;
    width: fit-content;
    margin: auto;
  }

  &:after {
    position: absolute;
    border-style: solid;
    content: "";
    width: 0;
    height: 0;
    left: 50%;
    margin-left: -5px;
    bottom: -5px;
    border-width: 5px 5px 0 5px;
    border-color: var(--blue090) transparent transparent transparent;
  }
`;

const Wrapper = styled.div`
  position: relative;

  &:hover ${Tooltip}, &:focus-within ${Tooltip} {
    opacity: 1;
  }
`;

const SelectRow = styled.div`
  display: grid;
  align-items: center;
  column-gap: 10px;
  grid-template-columns: 1fr auto;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
  color: var(--gray200);
  font-size: 14px;
`;

const StyledDescription = styled(Description)`
  margin-top: -20px;
  margin-bottom: 20px;
`;

const VariableInput = (props: Props) => {
  const {
    variable,
    variables,
    value,
    onChange,
    systemDomainId,
    label,
    config,
  } = props;
  const {
    dataType,
    required: requiredConfig,
    description: descriptionConfig,
    title: titleConfig,
  } = variable;
  const [currentSystemDomainId, setCurrentSystemDomainId] = useState<
    string | undefined
  >();
  const [disabled, setDisabled] = useState(false);

  const title = label ? label : titleConfig;
  const required = label ? false : requiredConfig;
  const description = label ? undefined : descriptionConfig;

  let safeValue = value;
  if (
    dataType.endsWith("_list") &&
    value !== undefined &&
    value !== null &&
    !Array.isArray(value)
  ) {
    safeValue = [];
  }

  useEffect(() => {
    setDisabled(false);
    variable.linkedVariables?.forEach(({ type, variableConfigId }) => {
      const value = variables.find(
        ({ variableConfigId: id }) => id === variableConfigId
      )?.value;
      if (value) {
        if (type === "system_domain_id") {
          const newSystemDomainId = value.toString();
          if (newSystemDomainId !== currentSystemDomainId) {
            setCurrentSystemDomainId(newSystemDomainId);

            if (currentSystemDomainId) {
              onChange(null);
            }
          }
        }
      } else {
        setDisabled(true);
      }
    });
  }, [variable.linkedVariables, variables, currentSystemDomainId]);

  const resolvedSystemDomainId = currentSystemDomainId || systemDomainId;

  // } else if (dataType === 'number' && value && isNaN(value)) {
  //   invalidType = 'number';
  // }

  // useEffect(() => {
  //   if (invalidType === 'list') {
  //     onChange([]);
  //   } else if (invalidType === 'number') {
  //     onChange(undefined);
  //   }
  // }, [invalidType, onChange]);

  const optionsAsync = (systemDomainId: string, dataType: string) => ({
    key: `${systemDomainId}/${dataType}`,
    fn: async () =>
      (
        await ApiService.fetch<{ data: Option[] }>(
          "GET",
          `/api/select_option/${systemDomainId}/${dataType}`
        )
      ).data,
  });

  const getInput = () => {
    if (dataType === "boolean") {
      return (
        <Checkbox
          label={title}
          value={safeValue}
          onChange={onChange}
          description={description}
        />
      );
    } else if (
      dataType === "number" ||
      dataType === "string" ||
      dataType === "string_multiline"
    ) {
      return (
        <>
          <Input
            label={title}
            required={required}
            value={safeValue ?? ""}
            onChange={onChange}
            numeric={dataType === "number"}
            textarea={dataType === "string_multiline"}
            height={dataType === "string_multiline" ? 180 : undefined}
          />
          {description && <StyledDescription label={description} />}
        </>
      );
    } else if (dataType === "string_list") {
      return (
        <ListInput
          label={title}
          value={safeValue ?? []}
          onChange={onChange}
          component={Input}
          newValue={() => ""}
          required={required}
          description={description}
        />
      );
    } else if (dataType === "dropdown") {
      return (
        <Select
          label={title}
          options={variable.options}
          value={safeValue}
          onChange={({ value }) => onChange(value)}
          onClear={() => onChange(undefined)}
          required={required}
          description={description}
          portalTarget={document.getElementById("PortalTarget")}
        />
      );
    } else if (dataType === "dropdown_list") {
      return (
        <Select
          label={title}
          options={variable.options}
          value={safeValue ?? []}
          onChange={(options) => onChange(options.map(({ value }) => value))}
          required={required}
          isMulti
          description={description}
          portalTarget={document.getElementById("PortalTarget")}
        />
      );
    } else if (dataType === "dropdown_option_list") {
      return (
        <ListInput
          label={title}
          value={safeValue ?? []}
          onChange={onChange}
          component={OptionInput}
          newValue={() => ({ label: "", value: "" })}
          required={required}
          description={description}
          labels={
            <TwoColumn>
              <span>Label:</span>
              <span>Value:</span>
            </TwoColumn>
          }
        />
      );
    } else if (dataType.startsWith("list_input_")) {
      const listInput = (
        "components" in config
          ? config.components.find(
              ({ type, id }) =>
                type === "list_input_config" &&
                id === dataType.replace("list_input_", "")
            )
          : undefined
      ) as ListInputConfig | undefined;
      if (listInput) {
        return (
          <ListInput
            label={title}
            value={safeValue ?? []}
            onChange={onChange}
            component={VariableInputForm}
            extraProps={{ config: listInput, noMargin: true }}
            newValue={() =>
              ({
                variables: [],
                listInputConfigId: listInput.id,
              } as ListInputInstance)
            }
            required={required}
            description={description}
          />
        );
      } else {
        return (
          <>
            <Input
              label={title}
              value={"Missing list input"}
              onChange={() => {}}
              disabled
            />
            {description && <StyledDescription label={description} />}
          </>
        );
      }
    } else if (dataType.endsWith("_list") && resolvedSystemDomainId) {
      return (
        <Select
          label={title}
          optionsAsync={optionsAsync(
            resolvedSystemDomainId,
            dataType.replace("_list", "")
          )}
          value={safeValue ?? []}
          onChange={(options) => onChange(options.map(({ value }) => value))}
          isMulti
          disabled={disabled}
          required={required}
          description={description}
          portalTarget={document.getElementById("PortalTarget")}
        />
      );
    } else if (dataType && resolvedSystemDomainId) {
      return (
        <SelectRow>
          <Select
            label={title}
            optionsAsync={optionsAsync(resolvedSystemDomainId, dataType)}
            value={safeValue}
            onChange={({ value }) => onChange(value)}
            onClear={() => onChange(undefined)}
            disabled={disabled}
            required={required}
            description={description}
            portalTarget={document.getElementById("PortalTarget")}
          />
          {dataType === "secret" && (
            <a
              href={`/config/organization/${systemDomainId}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                label="Manage Secrets"
                type="default"
                icon={ExternalLink}
              />
            </a>
          )}
        </SelectRow>
      );
    }

    return (
      <>
        <Input
          label={title}
          value={"Unknown data type " + dataType}
          onChange={() => {}}
          disabled
        />
        {description && <StyledDescription label={description} />}
      </>
    );
  };

  return (
    <Wrapper>
      {/* {description && (
        <Tooltip>
          <span>{description}</span>
        </Tooltip>
      )} */}
      {getInput()}
    </Wrapper>
  );
};

export default VariableInput;
